import useAuth from './useAuth';

// PARTNER PLATFORM S1 — who may do what on the partner surface.
//
// Each predicate below mirrors ONE @Auth list on the backend, quoted above it
// and read off the controller rather than off the epic document. They are
// separate hooks rather than one `useIsAdmin()` because the epic splits
// privilege TWO ways — the whole surface, and the platform switch — and a
// single predicate would either hide a control from somebody who owns it or
// show one that always refuses.
//
// ROLES DECIDE WHETHER A CONTROL EXISTS, NOT WHETHER IT 403s. A reviewer who
// presses a button and gets a 403 cannot tell a permission boundary from the
// several genuine 409s these endpoints also answer with, and this screen has no
// way to say which it was. So `false` means the control is ABSENT, not disabled.
//
// The role strings are the admin app's own lowercase-hyphen spellings (the ones
// RoleBasedGuard reads), NOT the BE's RolesEnum values. `super-admin` is checked
// explicitly in every predicate because these are plain functions — only
// RoleBasedGuard has the inheritance built in, and RolesGuard on the backend
// short-circuits on super-admin before any role list is consulted.
//
// THE `partner` ROLE NEVER APPEARS HERE. A partner is an external commercial
// counterparty: they sign into /v1/partner with their own module, they are
// absent from the staff role picker, and they hold no admin token.
//
// ── CUSTOMER-CARE HAS NO PARTNER ACCESS AT ALL ──────────────────────────
//
// An earlier draft of this file assumed the two LIST endpoints were
// @Auth(ADMIN, SUPER_ADMIN, CUSTOMER_CARE). THEY ARE NOT:
//
//   GET /v1/partner-applications   @Auth(ADMIN, SUPER_ADMIN)
//   GET /v1/admin/partners         @Auth(ADMIN, SUPER_ADMIN)
//
// Giving customer-care the nav entry and the route would have produced two
// screens that render a permission error from the API on every load — a false
// affordance, not a leak, but one that teaches an agent the tool is broken.

const rolesOf = (user: unknown): string[] => ((user as any)?.roles as string[]) ?? [];

/**
 * Can this account open the partner surface at all?
 *
 *   GET   /v1/partner-applications                       @Auth(ADMIN, SUPER_ADMIN)
 *   GET   /v1/partner-applications/:id                   @Auth(ADMIN, SUPER_ADMIN)
 *   POST  …/claim · …/release · …/approve · …/reject     @Auth(ADMIN, SUPER_ADMIN) + @NoImpersonation
 *   PATCH …/notes                                        @Auth(ADMIN, SUPER_ADMIN) + @NoImpersonation
 *   GET   /v1/admin/partners · /v1/admin/partners/:id    @Auth(ADMIN, SUPER_ADMIN)
 *   PATCH /v1/admin/partners/:id/status                  @Auth(ADMIN, SUPER_ADMIN) + @NoImpersonation
 *   GET|PUT  /v1/admin/partner-agreements/:id/draft      @Auth(ADMIN, SUPER_ADMIN)
 *   POST  /v1/admin/partner-agreements/:id/publish       @Auth(ADMIN, SUPER_ADMIN) + @NoImpersonation
 *   GET   …/versions · …/versions/:id · …/ledger         @Auth(ADMIN, SUPER_ADMIN)
 *
 * ONE list, because the backend uses one list. Splitting it here would invent a
 * boundary the API does not enforce, which is worse than having none: the
 * screen would imply a guarantee that a curl command walks straight past.
 */
export function useCanReviewPartners(): boolean {
  const { user } = useAuth();
  const roles = rolesOf(user);
  return roles.includes('admin') || roles.includes('super-admin');
}

/**
 * Can this account operate the platform's own switch?
 *
 *   PATCH /v1/admin/partners/settings   @Auth(SUPER_ADMIN) + @NoImpersonation
 *
 * READING the settings is ADMIN too — the amber "the public form is switched
 * OFF" banner has to render for every reviewer on every partner screen — but
 * FLIPPING it is not an editorial act. It is the one control on this surface
 * that changes what the outside world can do, with no deploy and no restart.
 */
export function useCanAdministerPartnerPlatform(): boolean {
  const { user } = useAuth();
  return rolesOf(user).includes('super-admin');
}

/**
 * Can this account break somebody ELSE'S claim?
 *
 *   POST /v1/partner-applications/:id/release
 *
 * The route is ADMIN+SUPER_ADMIN, but the SERVICE branches inside it: a
 * super-admin releases any application, anybody else may only release one they
 * hold themselves (the CAS filter carries their own user id, so a foreign
 * release is a 409, not a 403). This predicate exists so the queue can offer
 * "release" on a row held by a colleague only to the role that can actually do
 * it, instead of offering everyone a button that conflicts.
 */
export function useCanBreakPartnerClaim(): boolean {
  const { user } = useAuth();
  return rolesOf(user).includes('super-admin');
}

// ----------------------------------------------------------------------
// PARTNER PLATFORM S2 — the creator studio's admin review surface.
//
// The queue, the review page and FOUR of the five decisions sit on ONE @Auth
// list, which is the same list as S1's, so `useCanReviewPartners()` above
// already answers "may this account open the course review surface at all" and
// no fourth copy of it is added here. Read off §12.B, controller line by
// controller line:
//
//   GET  /v1/admin/partner-courses                       @Auth(ADMIN, SUPER_ADMIN)
//   GET  /v1/admin/partner-courses/:courseId             @Auth(ADMIN, SUPER_ADMIN)
//   POST …/:courseId/approve · …/reject · …/unpublish
//        · …/republish                                   @Auth(ADMIN, SUPER_ADMIN)
//   GET  …/revisions · …/revisions/:revisionId           @Auth(ADMIN, SUPER_ADMIN)
//   POST …/revisions/:revisionId/approve · …/reject      @Auth(ADMIN, SUPER_ADMIN)
//   GET  …/:courseId/media/:mediaId/playback             @Auth(ADMIN, SUPER_ADMIN)
//   GET  …/settings/course-defaults                      @Auth(ADMIN, SUPER_ADMIN)
//
// TWO endpoints are narrower, and each gets its own predicate below because a
// single one would either hide a control from somebody who owns it or show one
// that always refuses.
//
// CUSTOMER-CARE IS ABSENT FROM EVERY LIST ABOVE, exactly as in S1, so it is
// absent from the nav entry and from the route guard too.

/**
 * Can this account hand a partner's course back to Kayfa?
 *
 *   POST /v1/admin/partner-courses/:courseId/transfer-to-kayfa
 *     @Auth(SUPER_ADMIN) + @NoImpersonation
 *
 * The one action on this surface that changes who OWNS a row rather than
 * whether it is visible. It $unsets `partner` and the three partner timestamps,
 * leaving an ordinary Kayfa course editable in the legacy admin editor — which
 * is precisely the editor the S2 ownership refusal locks out of partner rows.
 * It is the supported end state for a terminated partner's live course, and it
 * is not reversible from this app: no route puts `partner` back.
 *
 * `false` means the button is ABSENT, not disabled. An ADMIN who presses a
 * disabled-looking control and gets a 403 cannot tell a permission boundary
 * from the several genuine 409s this endpoint also answers with.
 */
export function useCanTransferPartnerCourse(): boolean {
  const { user } = useAuth();
  return rolesOf(user).includes('super-admin');
}

/**
 * Can this account set the platform-owned parts of a partner course?
 *
 *   GET   /v1/admin/partner-courses/settings/course-defaults  @Auth(ADMIN, SUPER_ADMIN)
 *   PATCH /v1/admin/partner-courses/settings/course-defaults
 *     @Auth(SUPER_ADMIN) + @NoImpersonation
 *
 * READING them is ADMIN — every reviewer needs to see the amber "the studio is
 * not configured" banner, because until these are set the studio answers 503 to
 * course creation and the queue can never fill. WRITING them is not an editorial
 * act: the track, category, package, instructor byline and certificate template
 * chosen here are stamped onto every course any partner ever creates, and the
 * price caps are the only ceiling on what a partner may charge.
 */
export function useCanEditPartnerCourseDefaults(): boolean {
  const { user } = useAuth();
  return rolesOf(user).includes('super-admin');
}

// ----------------------------------------------------------------------
// BUNNY STREAM — the video operations surface (BUNNY_SPEC.md §9.D).
//
// EIGHT of the nine endpoints sit on the SAME @Auth list as the rest of this
// surface, so `useCanReviewPartners()` above already answers "may this account
// open the Bunny screen at all" and no fourth copy of it is added here. Read off
// §9.D, controller line by controller line:
//
//   GET  /v1/admin/bunny/status                    @Auth(ADMIN, SUPER_ADMIN)
//   GET  /v1/admin/bunny/videos                    @Auth(ADMIN, SUPER_ADMIN)
//   GET  /v1/admin/bunny/videos/:id                @Auth(ADMIN, SUPER_ADMIN)
//   POST …/:id/refresh · …/reencode · …/archive
//        · …/unarchive · …/detach                  @Auth(ADMIN, SUPER_ADMIN)
//
// ONE endpoint is narrower, and it gets its own predicate for the same reason
// the two above have theirs.
//
// CUSTOMER-CARE IS ABSENT FROM EVERY LIST, exactly as in S1 and S2, so it is
// absent from the nav entry and from the route guard too.

/**
 * Can this account destroy a Bunny video?
 *
 *   POST /v1/admin/bunny/videos/:id/purge   @Auth(SUPER_ADMIN)
 *
 * THE ONLY PATH IN THE ENTIRE EPIC THAT MAY REACH THE PROVIDER'S DELETE. Every
 * other removal on this surface is a refusal rather than a removal: archive is a
 * Kayfa-side state transition that deliberately does not call the provider, and
 * detach unlinks a pointer. Purge exists solely to clean up zero-byte orphans
 * left by a create the provider never answered, and the endpoint additionally
 * refuses anything that is not `failed`, anything that holds bytes, any request
 * without an explicit confirmation, and every request at all unless the server's
 * own purge flag is switched on.
 *
 * `false` means the control is ABSENT, not disabled — the posture
 * TransferCourseDialog uses for the other one-way door on this surface. An ADMIN
 * who presses a disabled-looking control and gets a 403 cannot tell a permission
 * boundary from the several genuine 409s and the 503 this same endpoint answers
 * with, and this screen has no way to say which it was.
 */
export function useCanPurgeBunnyVideo(): boolean {
  const { user } = useAuth();
  return rolesOf(user).includes('super-admin');
}
